import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseUrls } from '@app/app.constants';
import { Queue } from '@app/core/interfaces/c5/acd/queue';
import { QueueAgent } from '@app/core/interfaces/c5/acd/queue-agent';
import { Announcement } from '@app/core/interfaces/c5/announcement';
import { Device } from '@app/core/interfaces/c5/device';
import { Distribution } from '@app/core/interfaces/distribution';
import { ServiceUser } from '@app/core/interfaces/service-user';
import { UserLevel } from '@app/core/models/user-level';
import { EnhancedDevice } from '@app/shared/acd-cc/models/enhanced-device';
import { EnhancedQueue } from '@app/shared/acd-cc/models/enhanced-queue';
import { AcdCallCenterUtilsService } from '@app/shared/acd-cc/utils/acd-call-center-utils-service';
import { DeviceNamePipe } from '@app/shared/pipes/device-name';
import { Utils } from '@app/shared/services/utils.service';
import { Identifier, sortBy } from '@app/utils/common';
import { loggerFor } from '@app/utils/logger';
import { StringMap } from '@app/utils/string-map';
import { Agent } from '@business/acd/agent/models/agent';
import { Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AcdUtilsService {
  private logger = loggerFor('AcdUtilsService');

  constructor(
    private acdCallCenterUtilsService: AcdCallCenterUtilsService,
    private http: HttpClient,
    private utils: Utils,
    private deviceNamePipe: DeviceNamePipe
  ) {}

  public canReorderAgents(distribution: Distribution) {
    return distribution === Distribution.SERIAL || distribution === Distribution.CIRCULAR;
  }

  public downloadVoicemailPersonalAnnouncementURL(
    queueId: Identifier,
    callCenterId?: Identifier
  ): Observable<string> {
    let url: string;

    if (callCenterId) {
      url = `/callCenter/${callCenterId}/queue/${queueId}/voicemail/personalAnnouncement`;
    } else {
      url = `/acdQueue/${queueId}/voicemail/personalAnnouncement`;
    }

    return this.utils.apiUrl(url);
  }

  public enhanceDevice(device: Device): EnhancedDevice {
    return {
      ...device,
      nameForAcd: this.deviceNamePipe.transform(device),
    };
  }

  public enhanceQueue(queue: Queue, user: ServiceUser): EnhancedQueue {
    return this.acdCallCenterUtilsService.enhanceQueue(queue, user);
  }

  public extractCurrentRingingDeviceAndEnhanceDevices(agent: QueueAgent) {
    let currentRingingDevice: EnhancedDevice;
    let devices: EnhancedDevice[] = [];

    if (agent.userDevices != null) {
      devices = agent.userDevices.map((device) => this.enhanceDevice(device));

      if (agent.state?.device != null) {
        const device = devices.find((device) => device.sipUri === agent.state.device);

        if (device != null) {
          currentRingingDevice = device;
        } else {
          this.logger.error(
            `Invalid device with URI ${agent.state.device} configured a current device for user ${agent.user?.loginName} with id ${agent.userId}`
          );
        }
      }
    }

    return {
      currentRingingDevice,
      devices,
    };
  }

  public fetchAnnouncements(
    queueId: Identifier,
    domainAnnouncementsToo: boolean
  ): Observable<Announcement[]> {
    const params: StringMap<any> = {};

    if (domainAnnouncementsToo === true) {
      params.domainAnnouncementsToo = true;
    }

    return this.utils
      .apiUrl({
        [UserLevel.ServiceProviderAdmin]:
          BaseUrls.ServiceProvider.Enterprise + `/acdQueue/${queueId}/announcement`,
        [UserLevel.EnterpriseAdmin]: BaseUrls.Enterprise.Enterprise + `/acdQueue/${queueId}/announcement`,
        [UserLevel.EndUser]: `/user/acd/queue/${queueId}/announcement`,
      })
      .pipe(
        mergeMap((url) =>
          this.http.get<Announcement[]>(url, {
            params,
          })
        )
      );
  }

  /**
   * Return a new sorted array of agents.
   *
   * @param agents List of agents.
   * @param distribution Distribution to use when sorting.
   */
  public sortAgents(agents: Agent[], distribution?: Distribution): Agent[] {
    let sorted = agents.slice();

    if (distribution !== Distribution.SERIAL && distribution !== Distribution.CIRCULAR) {
      sorted = agents.sort(sortBy(['user.lastName', 'user.firstName']));
    }

    return sorted;
  }
}
